import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import type { ChecklistStatus, MarginBand, RecordLike } from '../../shared/checklistUtils';
import {
  getStatusLabel,
  isFiniteNumber,
  isPositiveNumber,
  normalizeString,
  readNumber,
  readString,
} from '../../shared/checklistUtils';

import type { EgsCapacityChecklistRow, EgsMarginChecklistRow } from './types';

const EGS_MARGIN_FIELD = 'Extra Guest Surcharges margin';
const EGS_CAPACITY_FIELD = 'EGS capacity';

const EXTRA_GUEST_CAPACITY_FIELDS = [
  'extra_guest_capacity',
  'extra_guest_cap',
  'extra_guest_count',
  'extra_guests',
  'max_extra_guests',
  'max_extra_guest_capacity',
  'egs_capacity',
];

const INCLUDED_CAPACITY_FIELDS = [
  'included_capacity',
  'included_guests',
  'max_included_guests',
  'max_guests_included',
  'number_of_guests',
];

const MAX_ROOM_CAPACITY_FIELDS = ['max_capacity', 'max_occupancy', 'capacity', 'max_guests'];
export function calculateMarginPercentage(costPrice?: number, sellPrice?: number) {
  if (!isPositiveNumber(sellPrice) || !isFiniteNumber(costPrice)) {
    return undefined;
  }

  return ((sellPrice - costPrice) / sellPrice) * 100;
}

export function getMarginBand(margin?: number): MarginBand | undefined {
  if (!isFiniteNumber(margin)) {
    return undefined;
  }

  if (margin <= 10) {
    return 'red';
  }

  if (margin >= 19) {
    return 'green';
  }

  return 'yellow';
}

export function getMarginStatus(marginBand?: MarginBand): ChecklistStatus {
  if (!marginBand) {
    return 'requires approval';
  }

  return marginBand === 'green' ? 'approved' : 'requires approval';
}

export function buildEgsMarginRows(
  relatedExtraGuestSurcharges: Array<OpportunityRelatedExtraGuestSurcharge> = [],
): Array<EgsMarginChecklistRow> {
  return getConfiguredEgsSurcharges(relatedExtraGuestSurcharges).map((relatedSurcharge) => {
    const costPrice =
      readNumber(relatedSurcharge, ['converted_cost__c']) ?? readNumber(relatedSurcharge, ['cost_price__c']);
    const sellPrice = readNumber(relatedSurcharge, ['sell_price_aud__c']);
    const sourceMargin = readNumber(relatedSurcharge, ['margin__c']);
    const margin = sourceMargin ?? calculateMarginPercentage(costPrice, sellPrice);
    const marginBand = getMarginBand(margin);
    const status = getMarginStatus(marginBand);

    return {
      ageRange: normalizeString(relatedSurcharge?.age_range__c),
      beddingType: normalizeString(relatedSurcharge?.other_extra_bedding_type__c || relatedSurcharge?.bedding_type__c),
      collectedBy: normalizeString(relatedSurcharge?.collected_by__c),
      id: `margin:extra_guest_surcharge__c:${String(relatedSurcharge.id)}`,
      costPrice,
      currencyCode: 'AUD',
      extraGuestType: normalizeString(relatedSurcharge?.extra_guest_type__c),
      field: EGS_MARGIN_FIELD,
      inclusions: normalizeString(relatedSurcharge?.inclusions__c),
      margin,
      marginBand,
      name: normalizeString(relatedSurcharge?.name) ?? String(relatedSurcharge.id),
      opportunityName: normalizeString(relatedSurcharge?.opportunity__c),
      sellPrice,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

export function buildEgsCapacityRows({
  hasEgsMarginRows,
  packages,
  propertyRoomTypes,
  relatedExtraGuestSurcharges,
  surcharges,
}: {
  hasEgsMarginRows?: boolean;
  packages?: Array<App.Package>;
  propertyRoomTypes?: Array<App.RoomType>;
  relatedExtraGuestSurcharges?: Array<OpportunityRelatedExtraGuestSurcharge>;
  surcharges: Array<SalesforceSurchargePeriod>;
}): Array<EgsCapacityChecklistRow> {
  const hasConfiguredEgsMarginRows =
    hasEgsMarginRows ?? getConfiguredEgsSurcharges(relatedExtraGuestSurcharges).length > 0;

  if (!hasConfiguredEgsMarginRows) {
    return [];
  }

  const surchargesEgsCapacity = getMaxDefinedNumber(
    surcharges.map((surcharge) => readNumber(surcharge as unknown as RecordLike, EXTRA_GUEST_CAPACITY_FIELDS)),
  );

  return (packages ?? []).map((offerPackage, index) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const roomTypeId = readString(packageRecord, ['fk_room_type_id']);
    const roomRateId = readString(packageRecord, ['fk_room_rate_id']);
    const roomType = propertyRoomTypes?.find((room) => String(room.id) === roomTypeId);
    const roomRate = findRoomRate(roomType, roomRateId);
    const includedCapacity =
      readNumber(packageRecord, INCLUDED_CAPACITY_FIELDS) ??
      readIncludedGuestsCapacity(roomRate) ??
      readNumber((roomType ?? {}) as RecordLike, ['max_included_guests']);
    const egsCapacity = readNumber(packageRecord, EXTRA_GUEST_CAPACITY_FIELDS) ?? surchargesEgsCapacity;
    const maxRoomCapacity = getMaxRoomCapacity(roomType);
    const status =
      isFiniteNumber(includedCapacity) && isFiniteNumber(egsCapacity) && isFiniteNumber(maxRoomCapacity)
        ? maxRoomCapacity === includedCapacity + egsCapacity
          ? 'approved'
          : 'requires approval'
        : 'requires approval';

    return {
      egsCapacity,
      field: EGS_CAPACITY_FIELD,
      id: `capacity:package:${String(
        offerPackage.id_salesforce_external ?? offerPackage.le_package_id ?? index,
      )}:room_type:${roomTypeId ?? ''}:room_rate:${roomRateId ?? ''}`,
      includedCapacity,
      maxRoomCapacity,
      name: offerPackage.deal_option_name || offerPackage.name || `Package ${index + 1}`,
      roomRateId,
      roomName: roomType?.name || 'Room not mapped',
      roomTypeId,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}
function getMaxRoomCapacity(roomType?: App.RoomType) {
  if (!roomType) {
    return undefined;
  }

  const roomTypeRecord = roomType as unknown as RecordLike;
  const capacityFromField = readNumber(roomTypeRecord, MAX_ROOM_CAPACITY_FIELDS);

  if (isFiniteNumber(capacityFromField)) {
    return capacityFromField;
  }

  const capacities = roomTypeRecord.capacities;
  if (!Array.isArray(capacities)) {
    return undefined;
  }

  return getMaxDefinedNumber(capacities.map(getCapacityTotal));
}

function findRoomRate(roomType?: App.RoomType, roomRateId?: string) {
  if (!roomType || !roomRateId || !Array.isArray(roomType.room_rates)) {
    return undefined;
  }

  return roomType.room_rates.find((roomRate) => String(roomRate.id) === roomRateId);
}

function readIncludedGuestsCapacity(roomRate?: App.RoomRate) {
  const includedGuests = (roomRate as unknown as RecordLike | undefined)?.included_guests;

  if (!Array.isArray(includedGuests)) {
    return undefined;
  }

  return getMaxDefinedNumber(includedGuests.map(getCapacityTotal));
}

function getCapacityTotal(value: unknown) {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }

  const record = value as RecordLike;
  const explicitTotal = readNumber(record, ['total', 'total_guests', 'max_guests']);

  if (isFiniteNumber(explicitTotal)) {
    return explicitTotal;
  }

  const adults = readNumber(record, ['adults']) ?? 0;
  const children = readNumber(record, ['children']) ?? 0;
  const teenagers = readNumber(record, ['teenagers']) ?? 0;

  return adults + children + teenagers;
}

function getMaxDefinedNumber(values: Array<number | undefined>) {
  const numericValues = values.filter(isFiniteNumber);

  return numericValues.length > 0 ? Math.max(...numericValues) : undefined;
}

export function getConfiguredEgsSurcharges(
  relatedExtraGuestSurcharges: Array<OpportunityRelatedExtraGuestSurcharge> = [],
) {
  return relatedExtraGuestSurcharges.filter((surcharge) => Boolean(surcharge.include__c) && !surcharge.archive__c);
}
